import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';

export type HistorialTipo = 'apu' | 'apu_adicional' | 'extra' | 'descuento';

export interface HistorialItemDTO {
  planilla_id: string;
  planilla_numero: number;
  planilla_estado: string;
  planilla_created_at: string;
  cabecera_id: string | null;
  cabecera_nombre: string | null;
  maestro_id: string;
  maestro_nombre: string;
  tipo: HistorialTipo;
  /** recipe_nombre for APU, descripcion for extras/descuentos */
  concepto: string;
  /** null for APU, categoria for extras, tipo for descuentos */
  categoria: string | null;
  mdo_material_id: string | null;
  mdo_material_nombre: string | null;
  cantidad: number | null;
  monto: number;
  unidad: string | null;
}

export interface ResumenCabeceraDTO {
  cabecera_id: string | null;
  cabecera_nombre: string | null;
  total_registros: number;
  total_monto: number;
  ultimo_estado_planilla: string | null;
}

export interface HistorialFilters {
  page?: number;
  limit?: number;
  cabeceraId?: string;
  maestroId?: string;
  recipeId?: string;
  mdoMaterialId?: string;
  estado?: string;
  fechaDesde?: string;
  fechaHasta?: string;
  tipo?: HistorialTipo;
  includeResumen?: 'true' | 'false';
}

interface HistorialResponse {
  items: HistorialItemDTO[];
  total: number;
  resumen: ResumenCabeceraDTO[];
}

// ── Paginated historial ──

export function usePlanillaHistorial(projectId: string, filters: HistorialFilters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== '') params.set(k, String(v));
  });

  return useQuery<HistorialResponse>({
    queryKey: queryKeys.planillaHistorial.byProject(projectId, filters as Record<string, unknown>),
    queryFn: () =>
      api
        .get<HistorialResponse>(`/projects/${projectId}/planilla-historial?${params.toString()}`)
        .then((r) => r.data),
    enabled: !!projectId,
  });
}

// ── PDF download ──

export interface HistorialPdfFilters extends Omit<HistorialFilters, 'page' | 'limit' | 'includeResumen'> {
  cabeceraLabel?: string;
  maestroLabel?: string;
  apuLabel?: string;
  mdoLabel?: string;
}

export function usePlanillaHistorialPdf(projectId: string) {
  return useMutation({
    mutationFn: async (filters: HistorialPdfFilters) => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== undefined && v !== '') params.set(k, String(v));
      });
      const res = await api.get<Blob>(
        `/projects/${projectId}/planilla-historial/pdf?${params.toString()}`,
        { responseType: 'blob' },
      );
      return res.data;
    },
    onSuccess: (blob, filters) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const suffix = filters.cabeceraLabel
        ? `-${filters.cabeceraLabel.toLowerCase().replace(/\s+/g, '-')}`
        : '';
      a.download = `historial-planillas${suffix}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    },
  });
}

// ── Resumen por cabecera (summary only) ──

export function useResumenPorCabecera(projectId: string) {
  return useQuery<ResumenCabeceraDTO[]>({
    queryKey: queryKeys.planillaHistorial.byProject(projectId, { includeResumen: 'true' }),
    queryFn: () =>
      api
        .get<HistorialResponse>(
          `/projects/${projectId}/planilla-historial?includeResumen=true&limit=1`,
        )
        .then((r) => r.data.resumen),
    enabled: !!projectId,
  });
}
